type TokenListener = () => void;

// PRTL-1356: renamed from "token" so every pre-hardening client is forced to
// re-login (old tokens have no server-side session and would be rejected).
export const TOKEN_STORAGE_KEY = "compass.authToken";
/**
 * PRTL-1348 masquerade tokens live in sessionStorage ONLY: per-tab, gone on
 * tab close, invisible to other tabs' storage listeners — so a masquerade
 * never clobbers the admin's own session in other tabs.
 */
export const MASQUERADE_STORAGE_KEY = "compass.masqueradeToken";

class TokenManager {
    private token: string | null = null;
    private masqueradeToken: string | null = null;
    private listeners = new Set<TokenListener>();

    constructor() {
        this.token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? null;
        this.masqueradeToken = sessionStorage.getItem(MASQUERADE_STORAGE_KEY) ?? null;
    }

    public get = () => {
        if (this.masqueradeToken) return this.masqueradeToken;
        return this.token === "" ? null : this.token;
    };

    public isMasquerading = () => this.masqueradeToken !== null;

    public setMasquerade = (token: string) => {
        this.masqueradeToken = token;
        sessionStorage.setItem(MASQUERADE_STORAGE_KEY, token);
        this.listeners.forEach((listener) => listener());
    };

    /** Exit masquerade; the admin's own token (if any) takes over again. */
    public clearMasquerade = () => {
        this.masqueradeToken = null;
        sessionStorage.removeItem(MASQUERADE_STORAGE_KEY);
        this.listeners.forEach((listener) => listener());
    };

    public set = (newToken: string | null) => {
        // A logout/401 while masquerading exits the masquerade only — the
        // admin's own token in localStorage must survive.
        if (newToken === null && this.masqueradeToken) {
            this.clearMasquerade();
            return;
        }
        this.token = newToken;
        if (newToken === null) {
            // removeItem (vs writing "") so other tabs' storage listeners see
            // newValue === null and treat it as a logout.
            localStorage.removeItem(TOKEN_STORAGE_KEY);
        } else {
            localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
        }
        this.listeners.forEach((listener) => listener());
    };

    public subscribe = (listener: TokenListener) => {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    };
}

export const tokenManager = new TokenManager();
